import { Button, Tooltip, Typography, styled } from '@mui/material';
import React, { useCallback, useState } from 'react';

import { DashboardT } from 'business/definitions';
import DefaultFiltersSharing from 'components/SharedItemUI/DefaultFiltersSharing';
import Flex from 'ui/Flex/Flex';
import Info from '@mui/icons-material/Info';
import Loading from 'components/Loading';
import useGetDashboardFilters from 'components/QueryPerimeter/hooks/useGetDashboardFilters';
import useGetDefaultDashboardFilters from 'hooks/useGetDefaultDashboardFilters';
import { useTranslation } from 'react-i18next';
import useUpdateDashboardDefaultFilter from 'hooks/useUpdateDashboardDefaultFilter';

const StyDefaultFiltersContainer = styled(Flex, {
    name: 'StyDefaultFiltersContainer',
})(({ theme }) => ({
    flexDirection: 'column',
    backgroundColor: theme.palette.primary.background,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    height: '100%',
}));

const StySharingSection = styled(Flex, {
    name: 'StySharingSection',
})(({ theme }) => ({
    padding: theme.spacing(1),
}));

const StyButton = styled(Button, {
    name: 'StyButton',
})(() => ({
    padding: '4px 8px',
    lineHeight: '1.75',
    borderRadius: '4px',
    minHeight: '2rem',
}));

const SharingModalDefaultFiltersWithData = ({
    dashboard,
    dashboardFilters,
    dashboardDefaultFilters,
}: {
    dashboard?: DashboardT;
    dashboardFilters?: DashboardFilterT[];
    dashboardDefaultFilters?: DashboardFilterT[];
}) => {
    const { t } = useTranslation();
    const updateDefaultFilters = useUpdateDashboardDefaultFilter();
    const [filtersEdit, setFiltersEdit] = useState<DashboardFilterT[]>(
        dashboardDefaultFilters ?? [],
    );
    const [canEditDefaultFilters, setCanEditDefaultFilters] = useState(false);

    const filters =
        dashboard && dashboardFilters
            ? dashboardFilters.filter(
                  (fltr) => fltr.dashboardKey === dashboard.key,
              )
            : [];

    const onFilterValueChange = (
        updatedProps: Partial<DashboardFilterT & WithScopes>,
        filterKey: number,
    ) => {
        if (filtersEdit) {
            const newFilters = filtersEdit.map((f) =>
                f.key === filterKey ? { ...f, ...updatedProps } : f,
            );
            setFiltersEdit(newFilters);
        }
    };

    const replaceByCurrentFilters = () => {
        setFiltersEdit(filters);
    };

    const onCancelFiltersEdit = useCallback(() => {
        setFiltersEdit(dashboardDefaultFilters ?? []);
        setCanEditDefaultFilters(false);
    }, [dashboardDefaultFilters]);

    const onSaveFiltersEdit = useCallback(() => {
        if (dashboard && dashboardDefaultFilters && filtersEdit) {
            filtersEdit.forEach((filter) => {
                const initialFilter = dashboardDefaultFilters.find(
                    (initFilter) => initFilter.key === filter.key,
                );
                if (JSON.stringify(filter) !== JSON.stringify(initialFilter))
                    updateDefaultFilters.mutate({
                        dashboard,
                        filterKey: filter.key,
                        updatedFilterProps: {
                            value: filter.value,
                            valueScale: filter.valueScale,
                            operation: filter.operation,
                        },
                    });
            });
            setCanEditDefaultFilters(false);
        }
    }, [dashboard, dashboardDefaultFilters, filtersEdit]);

    const defaultFiltersSharingView = (
        <DefaultFiltersSharing
            filters={filtersEdit}
            canEdit={canEditDefaultFilters}
            onFilterValueChange={onFilterValueChange}
        />
    );

    return (
        <StySharingSection direction="column">
            <Typography variant="h5" color="primary">
                {t('sharingModal.defaultFiltersSharingTitle')}
            </Typography>
            <StyDefaultFiltersContainer>
                <Flex direction="row">
                    <Typography>
                        {t('sharingModal.defaultFiltersSharingText')}
                    </Typography>
                    <Tooltip
                        title={t('sharingModal.defaultFiltersSharingInfo')}
                    >
                        <Info
                            color="primary"
                            fontSize="small"
                            sx={{ margin: '0 8px' }}
                        ></Info>
                    </Tooltip>
                </Flex>
                {defaultFiltersSharingView}
                <Flex justifyContent="right">
                    {canEditDefaultFilters ? (
                        <StyButton
                            variant="text"
                            size="small"
                            className="pendo-replace-default-filters-by-current"
                            onClick={() => replaceByCurrentFilters()}
                        >
                            {t('sharingModal.replaceByCurrentFilters')}
                        </StyButton>
                    ) : (
                        <StyButton
                            variant="contained"
                            size="small"
                            className="pendo-edit-default-filters"
                            onClick={() => setCanEditDefaultFilters(true)}
                        >
                            {t('sharingModal.updateDefaultFilters')}
                        </StyButton>
                    )}
                </Flex>
                {canEditDefaultFilters && (
                    <Flex flexDirection="column">
                        <Typography sx={{ padding: '20px' }}>
                            {t('sharingModal.replaceByCurrentFiltersText')}
                        </Typography>
                        <Flex flexDirection="row" justifyContent="right">
                            <Button
                                sx={{
                                    margin: '4px',
                                }}
                                variant="outlined"
                                size="small"
                                onClick={onCancelFiltersEdit}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                sx={{
                                    margin: '4px',
                                }}
                                variant="contained"
                                size="small"
                                className="pendo-save-default-filters"
                                onClick={onSaveFiltersEdit}
                            >
                                {t('settings.save')}
                            </Button>
                        </Flex>
                    </Flex>
                )}
            </StyDefaultFiltersContainer>
        </StySharingSection>
    );
};

const SharingModalDefaultFilters = ({
    dashboard,
}: {
    dashboard?: DashboardT;
}): JSX.Element => {
    const { data: dashboardFilters, isLoading: isGetDashboardFiltersLoading } =
        useGetDashboardFilters(dashboard);

    const {
        isLoading: isGetDashboardDefaultFiltersLoading,
        data: dashboardDefaultFilters,
    } = useGetDefaultDashboardFilters(dashboard?.slug);

    if (isGetDashboardDefaultFiltersLoading || isGetDashboardFiltersLoading) {
        return <Loading />;
    }

    return (
        <SharingModalDefaultFiltersWithData
            dashboard={dashboard}
            dashboardFilters={dashboardFilters}
            dashboardDefaultFilters={dashboardDefaultFilters}
        />
    );
};

export default SharingModalDefaultFilters;
